import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '../utils/crypto';
import { apiFamilyGet, apiFamilyInvite, apiFamilyCancelInvite, apiFamilyRemoveMember, apiFamilyLeave } from '../utils/api';

// Self-contained family-sharing panel — fetches and manages its own state so
// it can be dropped into the full ProfilePage or a standalone flyout alike.
// titleKey lets a caller pick which heading string to use. The default keeps
// the shared 'panel_title' (with its emoji) for the Vault's family flyout;
// ProfilePage passes 'panel_title_plain' because that page's section titles
// carry no emoji. The shared string is not edited to suit one page.
export default function FamilyPanel({ titleKey = 'panel_title' }) {
  const { t } = useTranslation('family');
  const [family, setFamily]         = useState(null);
  const [familyLoading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [familyMsg, setFamilyMsg]     = useState('');
  const [familyBusy, setFamilyBusy]   = useState(false);

  function loadFamily() {
    setLoading(true);
    apiFamilyGet().then(setFamily).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { loadFamily(); }, []);

  async function handleInvite(e) {
    e.preventDefault(); setFamilyMsg('');
    if (!inviteEmail.trim()) return;
    setFamilyBusy(true);
    try {
      await apiFamilyInvite(inviteEmail.trim());
      setInviteEmail('');
      setFamilyMsg(t('panel_msg_invite_sent'));
      loadFamily();
    } catch (err) { setFamilyMsg('⚠ ' + err.message); }
    finally { setFamilyBusy(false); }
  }

  async function handleCancelInvite(token) {
    setFamilyBusy(true); setFamilyMsg('');
    try { await apiFamilyCancelInvite(token); loadFamily(); }
    catch (err) { setFamilyMsg('⚠ ' + err.message); }
    finally { setFamilyBusy(false); }
  }

  async function handleRemoveMember(id, username) {
    if (!window.confirm(t('panel_confirm_remove', {username}))) return;
    setFamilyBusy(true); setFamilyMsg('');
    try { await apiFamilyRemoveMember(id); loadFamily(); }
    catch (err) { setFamilyMsg('⚠ ' + err.message); }
    finally { setFamilyBusy(false); }
  }

  async function handleLeaveFamily() {
    if (!window.confirm(t('panel_confirm_leave'))) return;
    setFamilyBusy(true); setFamilyMsg('');
    try { await apiFamilyLeave(); loadFamily(); }
    catch (err) { setFamilyMsg('⚠ ' + err.message); }
    finally { setFamilyBusy(false); }
  }

  if (familyLoading) return <div className="family-panel-loading">{t('panel_loading')}</div>;
  if (!family) return null;

  return (
    <div className="profile-card family-panel">
      <div className="profile-card-header">
        <div>
          <h3>{t(titleKey)}</h3>
          <p>
            {family.role === 'owner'
              ? t('panel_desc_owner')
              : t('panel_desc_member', {username: family.owner?.username})}
          </p>
        </div>
        <span className="status-badge on">
          {family.role === 'owner' ? t('panel_badge_owner') : t('panel_badge_member')}
        </span>
      </div>

      <div className="family-quota">
        <div className="vz-sq-bar"><div style={{ width: `${Math.min(100, (family.totalUsed / family.totalQuota) * 100)}%`, height: '100%', background: '#17A05C', borderRadius: 99 }} /></div>
        <span className="vz-sq-text">{t('panel_quota_shared', {used: formatBytes(family.totalUsed), quota: formatBytes(family.totalQuota)})}</span>
      </div>

      {family.role === 'owner' && !family.eligible && (
        <p className="profile-msg">{t('panel_not_eligible')}</p>
      )}

      {family.role === 'owner' && family.eligible && (
        <>
          <form onSubmit={handleInvite} className="family-invite-form">
            <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder={t('panel_invite_email_placeholder')} required
              disabled={family.members.length + family.pendingInvites.length >= family.maxMembers} />
            <button type="submit" className="btn-enable-2fa" disabled={familyBusy || family.members.length + family.pendingInvites.length >= family.maxMembers}>
              {familyBusy ? '…' : t('panel_invite_button')}
            </button>
          </form>
          <p className="family-hint">{t('panel_seats_used', {count: family.members.length + family.pendingInvites.length, max: family.maxMembers})}</p>

          {family.members.length > 0 && (
            <div className="profile-info-grid">
              {family.members.map(m => (
                <div key={m.id} className="profile-info-row family-member-row">
                  <span>@{m.username}</span>
                  <span className="family-member-used">{formatBytes(m.used)}</span>
                  <button className="btn-danger-sm" onClick={() => handleRemoveMember(m.id, m.username)} disabled={familyBusy}>{t('panel_remove_button')}</button>
                </div>
              ))}
            </div>
          )}

          {family.pendingInvites.length > 0 && (
            <div className="profile-info-grid">
              {family.pendingInvites.map(inv => (
                <div key={inv.token} className="profile-info-row family-member-row">
                  <span>{inv.email}</span>
                  <span className="family-member-used">{t('panel_status_pending')}</span>
                  <button className="btn-danger-sm" onClick={() => handleCancelInvite(inv.token)} disabled={familyBusy}>{t('panel_cancel_button')}</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {family.role === 'member' && (
        <button className="btn-logout" onClick={handleLeaveFamily} disabled={familyBusy}>
          {familyBusy ? '…' : t('panel_leave_family')}
        </button>
      )}

      {familyMsg && (
        <div className={`profile-msg ${familyMsg.startsWith('⚠') ? 'error' : 'success'}`}>{familyMsg}</div>
      )}
    </div>
  );
}
